import React from 'react';

import { gql, useQuery } from '@apollo/client';
import { Alert, Card, CardContent, Container, Divider, Stack, Typography } from '@mui/material';
import { find, includes, isEmpty, map } from 'lodash';
import { useParams } from 'react-router-dom';
import { BottomSafeArea, CardLevel, ContainerFlexHeight, LoadingSpinner } from '../../../components';
import { createScopedI18n } from '../../../i18n/i18n';
import PermissionPanel from './permission_panel';
import RestaurantPermissionCard from './restaurant_permission_card';

const usersDetailPageI18n = createScopedI18n('pages.users_detail');

const getRestaurantUserPermissionsGql = gql(`
  query getRestaurantUserPermissions($userId: ID) {
    user {
      id
      chain {
        id
        users(userId: $userId) {
          id
          username
          email
          roles
          restaurants {
            id
            name
            branchName
            permissions(userId: $userId) {
              id
              name
            }
          }
        }
      }
    }
  }
`);

type Restaurant = {
  id: string;
  name?: string | null;
  branchName?: string | null;
  permissions?: { id: string; name?: string | null }[] | null;
};

type User = {
  id: string;
  username?: string | null;
  email?: string | null;
  roles?: string[] | null;
  restaurants?: Restaurant[] | null;
};

type UsersData = {
  user: {
    id: string;
    chain?: {
      id: string;
      users?: User[];
    } | null;
  };
};

const UsersDetailPage = () => {
  const { id: userId } = useParams();

  const { data: userData, loading: userLoading } = useQuery<UsersData>(getRestaurantUserPermissionsGql, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const currentUserId = userData?.user.id;
  const user = React.useMemo(
    () => find(userData?.user?.chain?.users, ({ id }) => id === userId),
    [userData?.user?.chain?.users, userId],
  );
  const restaurants = React.useMemo(() => user?.restaurants ?? [], [user?.restaurants]);
  const canEditUser = React.useMemo(
    () => currentUserId !== user?.id && !includes(user?.roles, 'restaurant_root'),
    [currentUserId, user],
  );

  if (userLoading) {
    return <LoadingSpinner />;
  }

  if (isEmpty(user)) {
    return (
      <ContainerFlexHeight
        maxWidth="sm"
        sx={{ paddingY: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Alert severity="warning">{usersDetailPageI18n('blank')}</Alert>
      </ContainerFlexHeight>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ paddingY: 2 }}>
      <Stack gap={2}>
        <Stack flexDirection="row" gap={1}>
          <Stack flex={1} gap={1}>
            <Typography variant="h6" sx={{ paddingBottom: 1 }}>
              {usersDetailPageI18n('user_info')}
            </Typography>

            <Stack paddingLeft={1}>
              <Stack flexDirection="row" alignItems="baseline" gap={1}>
                <Stack flex={1}>
                  <Typography>{usersDetailPageI18n('phone_number')}</Typography>
                </Stack>
                <Stack flex={3}>
                  <Typography>{user?.username ?? ''}</Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <Stack gap={1}>
          <Typography variant="h6">{usersDetailPageI18n('manage_restaurants')}</Typography>
          <CardLevel variant="outlined" level={1}>
            <CardContent component={Stack} gap={2}>
              <Stack gap={1}>
                {isEmpty(restaurants) ? (
                  <CardLevel variant="outlined" level={2}>
                    <CardContent
                      component={Stack}
                      justifyContent="center"
                      alignItems="center"
                      sx={{ 'padding': 2, '&:last-child': { paddingBottom: 2 } }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {usersDetailPageI18n('blank_restaurant')}
                      </Typography>
                    </CardContent>
                  </CardLevel>
                ) : (
                  map(restaurants, (restaurant) => (
                    <RestaurantPermissionCard
                      key={restaurant.id}
                      userId={userId}
                      restaurant={restaurant}
                      disabled={!canEditUser}
                      editable={canEditUser}
                    />
                  ))
                )}
              </Stack>

              {canEditUser ? (
                <>
                  <Divider />
                  <Stack gap={1}>
                    <Card variant="outlined">
                      <CardContent component={Stack} gap={1}>
                        <Typography variant="body1">{usersDetailPageI18n('add_restaurant')}</Typography>
                        <PermissionPanel
                          hideRemoveButton
                          userId={userId}
                          editable={canEditUser}
                          excludeRestaurantIds={map(restaurants, 'id')}
                          addRestaurantUser
                          resetOnSuccess
                        />
                      </CardContent>
                    </Card>
                  </Stack>
                </>
              ) : null}
            </CardContent>
          </CardLevel>
        </Stack>
      </Stack>

      <BottomSafeArea />
    </Container>
  );
};

export default UsersDetailPage;
